import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { portfoliosApi, PORTFOLIO_IDS_MAX } from '@/lib/portfolioApi';
import { filterCustomerPortfolios, orderPortfolios } from '@/lib/portfolio';
import type { Portfolio, PortfolioMembers } from '@/lib/portfolio';

/**
 * Portfolio sources + rules for the pages that scope to a portfolio (Titles
 * Leaderboard today). ONE implementation so the picker list and member
 * resolution can never drift.
 *
 * Source: D360 CUSTOMER portfolios (v3 list filtered by lib/portfolio; members
 * via v1 `portfolio_items`, fetched only for the SELECTED portfolio). Both are
 * best-effort: a failure never throws up the tree or breaks the page. The list
 * query DOES surface a distinct `error` flag so the picker can say "Unavailable"
 * rather than being indistinguishable from a genuinely empty list. Ordered
 * My → Team (orderPortfolios).
 *
 * Unlike licensing this DROPS all default-resolution logic: there is no
 * resolveDefault, no env override, no preferences — the picker starts with
 * nothing selected and the user opts in.
 */
export type UsePortfolios = {
  /** My → Team ordered picker list (D360 customer portfolios). */
  portfolios: Portfolio[];
  /** True until the list has settled. */
  pending: boolean;
  /**
   * True when the list query FAILED (vs. simply resolving to an empty list).
   * Best-effort signal only — it never throws; it lets the picker distinguish
   * "Unavailable" (fetch failed) from "No portfolios" (genuinely empty).
   */
  error: boolean;
  /**
   * Member lists for the given portfolio ids via v1 `portfolio_items` (cached
   * 5 min). Failed lookups are dropped; throws only when EVERY lookup failed.
   */
  resolveMembers: (ids: string[]) => Promise<PortfolioMembers[]>;
};

export function usePortfolios(userEmail?: string | null): UsePortfolios {
  const queryClient = useQueryClient();

  const listQ = useQuery({
    // Let the query REJECT on failure (no try/catch swallow) so `isError`
    // actually means "the fetch failed". With retry:false this settles once and
    // the error stays contained to this hook — the component below reads it as a
    // flag, nothing throws up the tree.
    queryKey: ['portfolios', 'custom'],
    queryFn: async (): Promise<Portfolio[]> => filterCustomerPortfolios(await portfoliosApi.list()),
    retry: false,
    staleTime: 5 * 60_000,
  });

  const pending = listQ.isPending;
  const error = listQ.isError;
  const portfolios = useMemo(
    () => orderPortfolios(listQ.data ?? [], userEmail),
    [listQ.data, userEmail],
  );

  // FULL v1 membership — only ever for selected portfolios.
  const fetchMembers = useCallback(
    (id: string): Promise<PortfolioMembers | null> =>
      queryClient
        .fetchQuery({
          queryKey: ['portfolio-members', id],
          queryFn: () => portfoliosApi.members(id),
          staleTime: 5 * 60_000,
          retry: false,
        })
        .catch(() => null),
    [queryClient],
  );

  const resolveMembers = useCallback(
    async (ids: string[]): Promise<PortfolioMembers[]> => {
      const lists = await Promise.all(ids.map(fetchMembers));
      const ok = lists.filter((d): d is PortfolioMembers => d !== null);
      // Every lookup failed: surface it rather than reading as "no titles".
      if (ids.length > 0 && ok.length === 0) throw new Error('portfolio members unavailable');
      return ok;
    },
    [fetchMembers],
  );

  return { portfolios, pending, error, resolveMembers };
}

/**
 * Member resolution for the SELECTED portfolio ids as a query (cache key
 * `['portfolio-items', sortedIds]`), plus the sorted union of member parrot_ids
 * and the derived empty / too-large flags. `memberIds` is undefined until known.
 */
export function usePortfolioMembers(source: Pick<UsePortfolios, 'resolveMembers'>, ids: string[]) {
  const sortedIds = useMemo(() => [...ids].sort(), [ids]);
  const active = sortedIds.length > 0;
  const query = useQuery({
    queryKey: ['portfolio-items', sortedIds],
    queryFn: () => source.resolveMembers(sortedIds),
    enabled: active,
    retry: false,
  });
  const memberIds = useMemo<string[] | undefined>(() => {
    if (!active || !query.data) return undefined;
    const set = new Set<string>();
    for (const d of query.data) for (const id of d.portfolio_items) set.add(id);
    return [...set].sort();
  }, [active, query.data]);
  const empty = active && memberIds !== undefined && memberIds.length === 0;
  // The backend rejects > PORTFOLIO_IDS_MAX ids with a 400 — don't even ask.
  const tooLarge = active && memberIds !== undefined && memberIds.length > PORTFOLIO_IDS_MAX;
  return { query, memberIds, empty, tooLarge, active };
}
